import jsPDF from "jspdf";
import { hexToRgb, getWhiteLabelConfig } from "../../services/financeExport";

/**
 * 📄 EXPORTA UM ÚNICO LANÇAMENTO FINANCEIRO EM PDF
 * @param {Object} item - Lançamento financeiro
 * @param {Object} company - Dados da empresa (white label)
 * @param {string} usuario - Nome do usuário
 */
export function exportSingleFinancePDF(item, company, usuario = "Corretor") {
  
  // ==========================================================================
  // 🎨 CONFIGURAÇÃO WHITE LABEL
  // ==========================================================================
  const config = getWhiteLabelConfig(company);
  const primaryColor = hexToRgb(company?.cor || "#2563eb");
  const primary = [primaryColor.r, primaryColor.g, primaryColor.b];
  
  const today = new Date();
  const dataAtual = today.toLocaleDateString("pt-BR");
  const horaAtual = today.toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' });
  
  // Formatações do item
  const valorFormatado = Number(item.value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
  const dataFormatada = new Date(item.date + 'T12:00:00').toLocaleDateString('pt-BR');
  const tipo = item.type === 'receita' ? 'RECEITA' : 'DESPESA';
  const corTipo = item.type === 'receita' ? [34, 197, 94] : [239, 68, 68];

  // ==========================================================================
  // 📄 INICIALIZAÇÃO
  // ==========================================================================
  const doc = new jsPDF();
  
  // ===============================
  // 📌 MARCA D'ÁGUA
  // ===============================
  if (company?.mostrarMarcaDagua !== false) {
    doc.setGState(new doc.GState({ opacity: 0.1 }));
    doc.setFontSize(40);
    doc.setTextColor(200, 200, 200);
    doc.text((company?.nome || "IlhaCRM").toUpperCase(), 105, 140, {
      align: 'center',
      angle: 45
    });
    doc.setGState(new doc.GState({ opacity: 1 }));
  }

  // ===============================
  // 📌 HEADER CORPORATIVO
  // ===============================
  doc.setFillColor(...primary);
  doc.rect(0, 0, 210, 40, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(company?.nome || "IlhaCRM", 14, 18);

  if (company?.slogan) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(240, 240, 240);
    doc.text(company.slogan, 14, 26);
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text("COMPROVANTE DE LANÇAMENTO", 14, 35);

  // Info no canto direito
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(240, 240, 240);
  doc.text(`Emitido: ${dataAtual} ${horaAtual}`, 140, 15);
  doc.text(`Usuário: ${usuario}`, 140, 22);

  let y = 55;

  // ===============================
  // 📌 DESTAQUE DO VALOR
  // ===============================
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(...corTipo);
  doc.setLineWidth(1);
  doc.roundedRect(14, y, 182, 30, 3, 3, 'FD');
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text("VALOR", 105, y + 8, { align: 'center' });
  
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...corTipo);
  doc.text(valorFormatado, 105, y + 22, { align: 'center' });
  
  y += 45;

  // ===============================
  // 📌 DETALHES DO LANÇAMENTO
  // ===============================
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primary);
  doc.text("DETALHES DO LANÇAMENTO", 14, y);
  doc.setDrawColor(...primary);
  doc.setLineWidth(0.5);
  doc.line(14, y + 2, 105, y + 2);
  y += 10;

  // Grid de 2 colunas
  const detalhes = [
    { label: "Tipo", value: tipo },
    { label: "Categoria", value: item.category || "Geral" },
    { label: "Data", value: dataFormatada },
    { label: "ID", value: item.id },
    { label: "Status", value: item.status || "Concluído" }
  ];

  detalhes.forEach((det, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = 14 + (col * 95);
    const yPos = y + (row * 12);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(det.label, x, yPos);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(det.value, x, yPos + 5);
  });

  y += 40;

  // ===============================
  // 📌 DESCRIÇÃO
  // ===============================
  if (item.description || item.title) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text("DESCRIÇÃO", 14, y);
    doc.setDrawColor(...primary);
    doc.line(14, y + 2, 60, y + 2);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(15, 23, 42);
    
    const descricao = item.description || item.title || "Sem descrição";
    const linhas = doc.splitTextToSize(descricao, 180);
    doc.text(linhas, 14, y);
    
    y += (linhas.length * 5) + 10;
  }

  // ===============================
  // 📌 VÍNCULOS (imóvel/cliente)
  // ===============================
  if (item.propertyId || item.clientId) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text("VÍNCULOS", 14, y);
    doc.setDrawColor(...primary);
    doc.line(14, y + 2, 50, y + 2);
    y += 10;
    
    if (item.propertyId) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text("Imóvel:", 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(item.propertyId, 40, y);
      y += 7;
    }
    
    if (item.clientId) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text("Cliente:", 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(item.clientId, 40, y);
      y += 7;
    }
  }

  // ===============================
  // 📌 RODAPÉ
  // ===============================
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    doc.setDrawColor(...primary);
    doc.setLineWidth(0.3);
    doc.line(14, 280, 196, 280);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    
    let contactText = company?.nome || "IlhaCRM";
    if (company?.telefone) contactText += ` - ${company.telefone}`;
    if (company?.cnpj) contactText += ` - CNPJ: ${company.cnpj}`;
    
    doc.text(contactText, 14, 285);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text(`Página ${i}`, 180, 285);
    
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6);
    doc.setTextColor(180, 180, 180);
    doc.text('Documento gerado pelo sistema IlhaCRM', 14, 290);
  }

  // ===============================
  // 📌 DOWNLOAD
  // ===============================
  const nomeArquivo = `lancamento-${item.id}-${dataFormatada.replace(/\//g, '-')}.pdf`;
  doc.save(nomeArquivo);

  return doc;
}